import { ChangeEvent, ComponentProps, KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from 'react'

import { SendRightIcon } from '../../../icons'
import { SizeOptions } from '../../../types/types'
import { astraTwMerge } from '../../Utilities/astraTwMerge'
import { AstraButton } from '../AstraButton/AstraButton'
import { AstraInputWrapper, getInputDescribedBy, InputBase, inputTypographyStyles, textInputFrameCursorStyles } from '../AstraInputWrapper/AstraInputWrapper'
import { ASTRA_PRIVACY_BLUR_REVEAL_CLASS, useAstraPrivacyMode } from '../AstraPrivacyMode/AstraPrivacyMode'

type Props = {
    defaultValue?: string
    onEnterPressed?: (value: string) => void
    onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void
    onClick?: (value: string) => void
    size?: SizeOptions
    isValid?: (value: string) => boolean
    onSubmit?: (value: string) => void
    /** A fixed number of visible lines. Longer text scrolls. Takes precedence over `minRows` and `maxRows`. */
    rows?: number
    /** The number of lines the text area starts at before it grows with its content. */
    minRows?: number
    /** The number of lines the text area grows to before its content scrolls. */
    maxRows?: number
} & InputBase &
    Omit<ComponentProps<'textarea'>, 'defaultValue' | 'onClick'>

// Vertical padding that makes one line of text exactly fill the frame's minimum height, (frame height - 2px border - line
// height) / 2. The padding never changes, so the first line stays put as the text grows to more lines.
const verticalPadding: Record<SizeOptions, string> = {
    small: 'py-px',
    medium: 'py-[3px]',
    large: 'py-[7px]',
}

// Without a border the frame has 1px more room on each side.
const borderlessVerticalPadding: Record<SizeOptions, string> = {
    small: 'py-[2px]',
    medium: 'py-1',
    large: 'py-2',
}

const lineBoxStyles = (size: SizeOptions, borderless: boolean) =>
    astraTwMerge(inputTypographyStyles[size], borderless ? borderlessVerticalPadding[size] : verticalPadding[size])

// Takes up exactly the space of one line of text, padding included, so whatever it holds lines up with a line of text: the
// start icon with the first line and the send button with the last. The text area can't tell how many lines it shows, so
// they stay pinned there whether it shows one line or many.
const LineBox = ({ size, borderless, className, children }: { size: SizeOptions; borderless: boolean; className: string; children: ReactNode }) => (
    <span className={astraTwMerge('box-content flex min-h-[1lh] items-center', lineBoxStyles(size, borderless), className)}>{children}</span>
)

/**
 * A multi-line text input that grows with its content between `minRows` and `maxRows`, or stays at a fixed `rows`.
 * It can show a start icon and a send button for chat-style input, with `onEnterPressed` for submitting from the
 * keyboard. For a single line of text use AstraTextField.
 *
 * @summary for multi-line text entry that grows with its content
 */
export const AstraTextArea = ({
    defaultValue,
    helperText,
    errorText,
    onEnterPressed,
    onClick,
    onChange,
    isValid,
    size = 'medium',
    label,
    disabled,
    readOnly,
    error = false,
    fullWidth = false,
    startIcon,
    endIcon,
    rows,
    minRows,
    maxRows,
    borderless = false,
    name,
    placeholder,
    id,
    style,
    ...rest
}: Props) => {
    const generatedId = useId()
    const textAreaId = id ?? generatedId
    const [value, setValue] = useState<string | undefined>(defaultValue)
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const isPrivacyMode = useAstraPrivacyMode()

    const resetStateOnChange = () => {
        setValue(defaultValue)
    }

    useEffect(resetStateOnChange, [defaultValue])

    const handleKeyPress = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            const trimmed = event.currentTarget.value.trim()
            if (onEnterPressed && (!isValid || isValid(trimmed))) {
                onEnterPressed(trimmed)
                setValue('')
            }
        }
        event.stopPropagation()
    }

    const handleButtonClick = () => {
        value && isValid?.(value) && onClick?.(value)
        setValue('')
    }

    return (
        <AstraInputWrapper
            id={textAreaId}
            label={label}
            labelHtmlFor={textAreaId}
            helperText={helperText}
            errorText={errorText}
            error={error}
            disabled={disabled}
            readOnly={readOnly}
            fullWidth={fullWidth}
            size={size}
            endIcon={endIcon}
            className={textInputFrameCursorStyles(disabled)}
            onClick={() => textAreaRef.current?.focus()}
            borderless={borderless}
        >
            {startIcon && (
                <LineBox size={size} borderless={borderless} className="self-start">
                    {startIcon}
                </LineBox>
            )}
            <textarea
                ref={textAreaRef}
                id={textAreaId}
                name={name}
                rows={rows}
                disabled={disabled}
                readOnly={readOnly}
                aria-describedby={getInputDescribedBy({ id: textAreaId, helperText, errorText, error })}
                aria-invalid={error || undefined}
                className={astraTwMerge(
                    'box-content w-full resize-none self-center overflow-y-auto bg-transparent outline-none placeholder:text-subtle disabled:placeholder:text-disabled',
                    lineBoxStyles(size, borderless),
                    // Sizing to the content would otherwise also change the width as the user types. The containment keeps the
                    // width at the browser's default of about 20 characters, unless the frame is stretched wider.
                    !rows && 'min-w-[20ch] [contain:inline-size] [field-sizing:content]',
                    isPrivacyMode && ASTRA_PRIVACY_BLUR_REVEAL_CLASS,
                    error && !disabled && 'text-critical'
                )}
                onChange={e => {
                    setValue(e.target.value)
                    onChange && onChange(e)
                }}
                value={value}
                placeholder={placeholder}
                onKeyDown={handleKeyPress}
                {...rest}
                style={rows ? style : { minHeight: minRows ? `${minRows}lh` : undefined, maxHeight: maxRows ? `${maxRows}lh` : undefined, ...style }}
            />

            {onClick && (
                <LineBox size={size} borderless={borderless} className={astraTwMerge('self-end', size === 'small' ? 'mr-[-.5rem]' : 'mr-[-.25rem]')}>
                    <AstraButton
                        disabled={!value || value.trim().length < 1 || disabled || readOnly}
                        icon={<SendRightIcon />}
                        size="small"
                        onClick={handleButtonClick}
                    />
                </LineBox>
            )}
        </AstraInputWrapper>
    )
}
